import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { User } from '../models/User';
import api from '../services/api';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  accessToken: string | null;
  tokenExpiration: string | null; // Dodana właściwość tokenExpiration
  login: (email: string, password: string) => Promise<void>;
  register: (
    name: string,
    surname: string,
    email: string,
    password: string,
  ) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (password: string, token: string) => Promise<void>;
  confirmEmail: (token: string) => Promise<void>;
  logout: () => void;
  loginWithGoogle: (accessToken: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [accessToken, setAccessToken] = useState<string | null>(
    localStorage.getItem('accessToken')
  );
  const [tokenExpiration, setTokenExpiration] = useState<string | null>(null);

  // Zapisz token w localStorage gdy się zmieni
  useEffect(() => {
    if (accessToken) {
      localStorage.setItem('accessToken', accessToken);
    } else {
      localStorage.removeItem('accessToken');
    }
  }, [accessToken]);

  // Pobierz dane użytkownika po załadowaniu strony
  const fetchUser = useCallback(async () => {
    if (!accessToken) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.get('/auth/me');

      if (response.status === 200) {
        setUser(response.data.user);
        setIsAuthenticated(true);
        
        // Pobierz informację o wygaśnięciu tokenu, jeśli jest dostępna
        if (response.data.tokenExpiration) {
          setTokenExpiration(response.data.tokenExpiration);
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setTokenExpiration(null);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      setUser(null);
      setIsAuthenticated(false);
      setAccessToken(null); // Usuwamy token jeśli jest nieważny
      setTokenExpiration(null);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const confirmEmail = async (token: string) => {
    try {
      const response = await api.post('/auth/confirm', { token });

      if (response.status !== 200) {
        throw new Error('Błąd podczas potwierdzania emaila');
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error('Błąd podczas potwierdzania emaila');
      } else {
        throw new Error('Błąd serwera');
      }
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      const response = await api.post('/auth/forgot-password', { email });

      if (response.status !== 200) {
        throw new Error('Błąd podczas wysyłania maila');
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error('Błąd podczas wysyłania maila');
      } else {
        throw new Error('Błąd serwera');
      }
    }
  };

  const resetPassword = async (password: string, token: string) => {
    try {
      const response = await api.post('/auth/reset-password', {
        newPassword: password,
        token,
      });

      if (response.status !== 200) {
        throw new Error('Błąd podczas resetowania hasła');
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error('Błąd podczas resetowania hasła');
      } else {
        throw new Error('Błąd serwera');
      }
    }
  };

  const register = async (
    name: string,
    surname: string,
    email: string,
    password: string,
  ) => {
    try {
      const response = await api.post('/auth/register', {
        name,
        surname,
        email,
        password,
      });

      if (response.status !== 201 && response.status !== 200) {
        throw new Error('Błąd podczas rejestracji');
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error('Błąd podczas rejestracji');
      } else {
        throw new Error('Błąd serwera');
      }
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      
      // Zapisz access token
      setAccessToken(response.data.accessToken);
      
      // Zapisz informację o wygaśnięciu tokenu, jeśli jest dostępna
      if (response.data.tokenExpiration) {
        setTokenExpiration(response.data.tokenExpiration);
      }
      
      // Zapisz dane użytkownika
      setUser(response.data.user);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Niezależnie od wyniku, usuń dane użytkownika i token
      setUser(null);
      setIsAuthenticated(false);
      setAccessToken(null);
      setTokenExpiration(null);
    }
  };

  const loginWithGoogle = async (googleAccessToken: string) => {
    try {
      const response = await api.post('/auth/google', {
        token: googleAccessToken,
      });

      // Zapisz access token
      setAccessToken(response.data.accessToken);
      
      // Zapisz informację o wygaśnięciu tokenu, jeśli jest dostępna
      if (response.data.tokenExpiration) {
        setTokenExpiration(response.data.tokenExpiration);
      }
      
      // Zapisz dane użytkownika
      setUser(response.data.user);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Login with Google error:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        resetPassword,
        confirmEmail,
        forgotPassword,
        isAuthenticated,
        loading,
        accessToken,
        tokenExpiration,
        login,
        register,
        logout,
        loginWithGoogle,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
